/**
 * Convert stored HTML (e.g. AI whiteboard / CKEditor) to plain text for textareas.
 * List markup is removed but list items become lines with • or numbers.
 * Also treats common non-semantic patterns: ul/li, and indented p/div (margin/padding).
 */

function decorateListItemsForPlainText(root) {
	const doc = root.ownerDocument;
	if (!doc) return;
	root.querySelectorAll('li').forEach((li) => {
		const list = li.parentElement;
		if (!list) return;
		const tag = list.tagName;
		if (tag !== 'UL' && tag !== 'OL') return;
		let prefix;
		if (tag === 'UL') {
			prefix = '• ';
		} else {
			const items = Array.from(list.children).filter((c) => c.tagName === 'LI');
			const index = items.indexOf(li);
			prefix = `${index + 1}. `;
		}
		li.insertBefore(doc.createTextNode(prefix), li.firstChild);
		li.appendChild(doc.createTextNode('\n'));
	});
}

/** Pixels-ish indent from inline style (margin-left, padding-left, text-indent). */
function indentFromStyleAttr(el) {
	const s = el.getAttribute('style') || '';
	let max = 0;
	const re =
		/(?:margin-left|padding-left|text-indent)\s*:\s*(\d+(?:\.\d+)?)\s*(px|em|rem)/gi;
	let m;
	while ((m = re.exec(s)) !== null) {
		const val = parseFloat(m[1], 10);
		const unit = m[2].toLowerCase();
		const px = unit === 'em' ? val * 16 : unit === 'rem' ? val * 16 : val;
		max = Math.max(max, px);
	}
	return max;
}

function hasBlockChild(el) {
	return Array.from(el.children).some((c) =>
		['P', 'DIV', 'UL', 'OL', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE'].includes(
			c.tagName
		)
	);
}

function alreadyBulletLikeText(text) {
	return /^\s*[•\*\-–]\s/.test(text) || /^\s*\d+\.\s/.test(text);
}

/**
 * GPT often emits <h3>Takeaways</h3> then bare <p> lines instead of <ul><li>.
 * Turn those consecutive paragraphs (until the next h3) into bulleted lines.
 */
function decorateTakeawaysParagraphsAsBullets(root) {
	const doc = root.ownerDocument;
	if (!doc) return;

	const bulletParagraph = (p) => {
		if (p.closest('li')) return;
		const t = p.textContent || '';
		if (alreadyBulletLikeText(t)) return;
		p.insertBefore(doc.createTextNode('• '), p.firstChild);
		p.appendChild(doc.createTextNode('\n'));
	};

	root.querySelectorAll('h3').forEach((h3) => {
		if (!/^takeaways$/i.test((h3.textContent || '').trim())) return;
		const next = h3.nextElementSibling;
		if (
			next &&
			next.tagName === 'DIV' &&
			!next.querySelector('ul, ol, h1, h2, h3, h4, h5, h6') &&
			next.children.length > 0 &&
			Array.from(next.children).every((c) => c.tagName === 'P')
		) {
			Array.from(next.children).forEach((c) => {
				if (c.tagName === 'P') bulletParagraph(c);
			});
			return;
		}
		let el = next;
		while (el && el.tagName !== 'H3') {
			if (el.tagName === 'P') bulletParagraph(el);
			el = el.nextElementSibling;
		}
	});
}

/** Indented p/div (AI / editor “fake lists”) → prefix • */
function decorateIndentedBlocksAsBullets(root) {
	const doc = root.ownerDocument;
	if (!doc) return;
	root.querySelectorAll('p, div').forEach((el) => {
		if (el.closest('li')) return;
		if (indentFromStyleAttr(el) < 16) return;
		if (hasBlockChild(el)) return;
		const text = el.textContent || '';
		if (alreadyBulletLikeText(text)) return;
		el.insertBefore(doc.createTextNode('• '), el.firstChild);
		el.appendChild(doc.createTextNode('\n'));
	});
}

/** Lines that start with 2+ spaces, NBSPs, or tab(s) (fake lists / pasted notes). */
function bulletizeSpaceIndentedLines(text) {
	return text
		.split('\n')
		.map((line) => {
			const m = line.match(/^([\s\u00a0]{2,}|\t+)(\S.*)$/);
			if (!m) return line;
			const body = m[2];
			if (alreadyBulletLikeText(body)) return line;
			return `• ${body}`;
		})
		.join('\n');
}

export default function htmlToPlainTextWithLists(html) {
	if (html == null || typeof html !== 'string') return '';
	const noScripts = html.replace(
		/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
		''
	);
	const withBreaks = noScripts
		.replace(/<br\s*\/?>/gi, '\n')
		.replace(/<\/(h[1-6]|p|div|ul|ol|tr|section|article|blockquote|pre)>/gi, '\n')
		.replace(
			/<(h[1-6]|p|div|ul|ol|tr|section|article|blockquote|pre)\b[^>]*>/gi,
			'\n'
		);
	const wrapped = `<div>${withBreaks}</div>`;
	const doc = new DOMParser().parseFromString(wrapped, 'text/html');
	decorateListItemsForPlainText(doc.body);
	decorateIndentedBlocksAsBullets(doc.body);
	decorateTakeawaysParagraphsAsBullets(doc.body);
	let out = doc.body.textContent || '';
	out = bulletizeSpaceIndentedLines(out);
	out = out.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
	out = out.replace(/<\/?[a-zA-Z][\w-]*(?:\s[^>]*)?>/g, '');
	return out;
}
